// Each browser tab keeps its own session (sessionStorage), so two tabs of the same
// browser can be signed in with two different accounts. localStorage only remembers
// the last account that signed in, to open new tabs already signed in.
const TOKEN_STORAGE_KEY = "token";
const PERSISTENCE_STORAGE_KEY = "xima_auth_persistence";
const TAB_SIGNED_OUT_KEY = "xima_tab_signed_out";
const PERSISTENCE_LOCAL = "local";
const PERSISTENCE_SESSION = "session";

const getStorageByScope = (scope) => {
  if (typeof window === "undefined") return null;
  try {
    if (scope === PERSISTENCE_SESSION) return window.sessionStorage;
    return window.localStorage;
  } catch (error) {
    return null;
  }
};

const readItem = (storageRef, key) => {
  if (!storageRef) return "";
  try {
    return String(storageRef.getItem(key) || "").trim();
  } catch (error) {
    return "";
  }
};

const writeItem = (storageRef, key, value) => {
  if (!storageRef) return;
  try {
    storageRef.setItem(key, value);
  } catch (error) {
    // Ignore storage access errors.
  }
};

const removeItem = (storageRef, key) => {
  if (!storageRef) return;
  try {
    storageRef.removeItem(key);
  } catch (error) {
    // Ignore storage access errors.
  }
};

// Signs out this tab only. The remembered account is forgotten only when it is
// the same one this tab was using, so another tab with another account stays in.
export const clearStoredAuthToken = () => {
  const sessionStorageRef = getStorageByScope(PERSISTENCE_SESSION);
  const localStorageRef = getStorageByScope(PERSISTENCE_LOCAL);
  const tabToken = readItem(sessionStorageRef, TOKEN_STORAGE_KEY);
  const rememberedToken = readItem(localStorageRef, TOKEN_STORAGE_KEY);
  removeItem(sessionStorageRef, TOKEN_STORAGE_KEY);
  writeItem(sessionStorageRef, TAB_SIGNED_OUT_KEY, "1");
  if (!tabToken || tabToken === rememberedToken) {
    removeItem(localStorageRef, TOKEN_STORAGE_KEY);
    removeItem(localStorageRef, PERSISTENCE_STORAGE_KEY);
  }
};

export const getStoredAuthToken = () => {
  const sessionStorageRef = getStorageByScope(PERSISTENCE_SESSION);
  const tabToken = readItem(sessionStorageRef, TOKEN_STORAGE_KEY);
  if (tabToken) return tabToken;
  // A tab that signed out does not pick up the account of another tab.
  if (readItem(sessionStorageRef, TAB_SIGNED_OUT_KEY)) return "";
  const rememberedToken = readItem(getStorageByScope(PERSISTENCE_LOCAL), TOKEN_STORAGE_KEY);
  // A new tab starts with the remembered account and keeps it from then on.
  if (rememberedToken) writeItem(sessionStorageRef, TOKEN_STORAGE_KEY, rememberedToken);
  return rememberedToken;
};

export const storeAuthToken = (token, { persist = true } = {}) => {
  const normalizedToken = String(token || "").trim();
  const sessionStorageRef = getStorageByScope(PERSISTENCE_SESSION);
  const localStorageRef = getStorageByScope(PERSISTENCE_LOCAL);
  removeItem(sessionStorageRef, TAB_SIGNED_OUT_KEY);
  if (!normalizedToken) {
    clearStoredAuthToken();
    return "";
  }

  writeItem(sessionStorageRef, TOKEN_STORAGE_KEY, normalizedToken);
  if (persist) {
    writeItem(localStorageRef, TOKEN_STORAGE_KEY, normalizedToken);
    writeItem(localStorageRef, PERSISTENCE_STORAGE_KEY, PERSISTENCE_LOCAL);
  }
  return normalizedToken;
};

// "Entrar con otra cuenta" in this tab: forgets this tab's session locally,
// without signing the account out on the server (other tabs keep it).
export const forgetThisTabSession = () => {
  const sessionStorageRef = getStorageByScope(PERSISTENCE_SESSION);
  removeItem(sessionStorageRef, TOKEN_STORAGE_KEY);
  writeItem(sessionStorageRef, TAB_SIGNED_OUT_KEY, "1");
};

// Open tabs announce which session they use, so signing out in one tab does not
// close the account in another tab that is still using it.
const TAB_REGISTRY_KEY = "xima_open_tabs";
const TAB_ALIVE_MS = 15 * 1000;
const tabId =
  typeof window !== "undefined"
    ? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
    : "server";
const tokenFingerprint = (token) => String(token || "").slice(-24);

const readTabRegistry = () => {
  try {
    const parsed = JSON.parse(readItem(getStorageByScope(PERSISTENCE_LOCAL), TAB_REGISTRY_KEY) || "{}");
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    return {};
  }
};

const writeTabRegistry = (registry) => {
  const nowMs = Date.now();
  const alive = {};
  Object.entries(registry).forEach(([key, entry]) => {
    if (entry && nowMs - Number(entry.at || 0) < TAB_ALIVE_MS) alive[key] = entry;
  });
  writeItem(getStorageByScope(PERSISTENCE_LOCAL), TAB_REGISTRY_KEY, JSON.stringify(alive));
};

export const announceThisTab = () => {
  const registry = readTabRegistry();
  const token = readItem(getStorageByScope(PERSISTENCE_SESSION), TOKEN_STORAGE_KEY);
  if (token) registry[tabId] = { session: tokenFingerprint(token), at: Date.now() };
  else delete registry[tabId];
  writeTabRegistry(registry);
};

export const forgetThisTabInRegistry = () => {
  const registry = readTabRegistry();
  delete registry[tabId];
  writeTabRegistry(registry);
};

export const isSessionOpenInAnotherTab = (token) => {
  const fingerprint = tokenFingerprint(token);
  if (!fingerprint) return false;
  const nowMs = Date.now();
  return Object.entries(readTabRegistry()).some(
    ([key, entry]) =>
      key !== tabId && entry?.session === fingerprint && nowMs - Number(entry.at || 0) < TAB_ALIVE_MS
  );
};
